/**
 * Workspace store — pure state for open tabs in two pane groups.
 *
 * Each pane group (primary, secondary) has its own tabs and active tab.
 * The store tracks which group is focused (receives SideNav clicks).
 * It does NOT call navigate() — that's useWorkspaceNav's job.
 *
 * Invariants:
 * - Primary group always has >= 1 tab.
 * - Secondary group can be empty (no split). When its last tab closes,
 *   the group is removed and the app returns to single-pane mode.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { ATTENTION_ROUTES } from '@shared/constants/routes';
import { destinationById, destinationForPath } from '@shared/navigation/destinations';


import {
  rewriteAttentionTabs,
  rewriteChatNewTabs,
  rewriteNutritionTabs,
  rewriteSettingsTabs,
} from './migrateTabs';
import { dedupePrimaryTabs } from './placeKey';

import type { PaneGroup, WorkspaceTab } from '../types';

interface WorkspaceState {
  tabs: WorkspaceTab[];
  /** Active tab ID per group. */
  primaryActiveTabId: string;
  secondaryActiveTabId: string | null;
  /** Which group receives SideNav navigation. */
  focusedGroup: PaneGroup;
  /** Primary pane width ratio (0–1). Default 0.5. */
  splitRatio: number;
}

interface WorkspaceActions {
  /** Focus a group (it receives SideNav clicks). */
  setFocusedGroup: (group: PaneGroup) => void;
  /** Set the active tab within a group. */
  setActiveTabId: (group: PaneGroup, id: string) => void;
  /** Add a tab to a group. */
  addTab: (tab: WorkspaceTab) => void;
  /** Close a tab. If it's the last in secondary, closes the secondary pane. */
  closeTab: (id: string) => void;
  /** Update one tab's title and path together; the path change re-derives destinationId. */
  updateTab: (tabId: string, changes: TabLocation) => void;
  /** Toggle a tab's pinned state within its group. */
  togglePin: (id: string) => void;
  /** Reorder tabs within the same zone (pinned/unpinned) of the same group. */
  reorderTabs: (activeId: string, overId: string) => void;
  /** Move a tab to the secondary pane (opens split if not open). */
  moveToSecondary: (tabId: string) => void;
  /** Close the secondary pane entirely — moves all secondary tabs to primary. */
  closeSidePane: () => void;
  /** Update the split ratio. */
  setSplitRatio: (ratio: number) => void;
  /**
   * Open-or-focus a catalog destination in the primary group without touching
   * the URL: the group's active tab if it shows the destination, else the
   * leftmost tab that does, else a new tab. Never rewrites an existing tab —
   * that is what makes the strip trustworthy as memory (nav spec §4.3). Used by
   * an installed app's launch, which runs before the router exists; the
   * in-app path is `useWorkspaceNav.openDestination`, which also navigates.
   */
  openOrFocusDestination: (destinationId: string) => void;
}

type WorkspaceStore = WorkspaceState & WorkspaceActions;

/** What a navigation tells a tab: where it went, what it is called, what it carried. */
export interface TabLocation {
  title: string;
  path: string;
  /** React Router's `location.state`. Omitted and `undefined` both mean none. */
  state?: unknown;
  /** Supplied only when the caller already has the key; otherwise one is minted. */
  locationKey?: string;
}

let locationKeySeq = 0;

/**
 * A fresh `location.key` for a tab's new entry.
 *
 * Prefixed by the tab so two tabs can never collide — the constant `'pane'`
 * they used to share made every pane tab indistinguishable — and counted so a
 * second navigation inside one tab is a different entry, which is what an
 * effect keyed on `location.key` is watching for.
 */
function mintLocationKey(tabId: string): string {
  locationKeySeq += 1;
  return `${tabId}-${locationKeySeq}`;
}

/** Whether a navigation would leave the tab exactly where it already is. */
function isSameLocation(tab: WorkspaceTab, next: TabLocation): boolean {
  return tab.title === next.title && tab.path === next.path && tab.state === next.state;
}

/**
 * A tab as it goes to storage: without the fields that belong to this session.
 *
 * Navigation state and the location key are runtime-only. Tabs keep their state
 * for a session and no longer, and an arbitrary navigation payload — whatever a
 * caller happened to pass to `navigate()` — is not something to serialise into
 * localStorage and hand back days later.
 */
function forStorage(tab: WorkspaceTab): WorkspaceTab {
  const { state: _state, locationKey: _key, ...rest } = tab;
  return rest;
}

/** A tab with its destinationId recomputed from its path; the key is absent when nothing matches. */
function withDestination(tab: WorkspaceTab): WorkspaceTab {
  const { destinationId: _stale, ...base } = tab;
  const id = destinationForPath(tab.path)?.id;
  return id === undefined ? base : { ...base, destinationId: id };
}

const INITIAL_TAB: WorkspaceTab = withDestination({
  id: 'tab-initial',
  title: 'Inbox',
  path: ATTENTION_ROUTES.root,
  pinned: false,
  group: 'primary',
});

/** Get tabs for a specific group. */
export function tabsForGroup(tabs: WorkspaceTab[], group: PaneGroup): WorkspaceTab[] {
  return tabs.filter(t => t.group === group);
}

/** Get the active tab ID for a group. */
function getActiveId(state: WorkspaceState, group: PaneGroup): string | null {
  return group === 'primary' ? state.primaryActiveTabId : state.secondaryActiveTabId;
}

/**
 * The tab in `group` to focus for a destination: the group's active tab if it
 * shows that destination, else the leftmost tab that does. Several tabs can
 * legitimately share a destinationId (two Notes documents), and clicking the
 * rail must not jump the user off the one they are on.
 */
export function matchingTab(
  tabs: WorkspaceTab[],
  group: PaneGroup,
  activeId: string | null,
  destinationId: string
): WorkspaceTab | undefined {
  const candidates = tabsForGroup(tabs, group).filter(t => t.destinationId === destinationId);
  return candidates.find(t => t.id === activeId) ?? candidates[0];
}

/**
 * The state patch for `openOrFocusDestination`, computed as a pure function
 * of the current tabs so the store action itself stays a one-liner. `null`
 * for an unknown destination — the caller leaves state untouched.
 */
function openOrFocusPatch(
  tabs: WorkspaceTab[],
  primaryActiveTabId: string,
  destinationId: string
): Pick<WorkspaceState, 'tabs' | 'primaryActiveTabId' | 'focusedGroup'> | null {
  const dest = destinationById(destinationId);
  if (!dest) return null;

  const existing = matchingTab(tabs, 'primary', primaryActiveTabId, destinationId);
  if (existing) {
    return { tabs, primaryActiveTabId: existing.id, focusedGroup: 'primary' };
  }

  const id = `tab-${Date.now()}`;
  const newTab = withDestination({ id, title: dest.label, path: dest.path, pinned: false, group: 'primary' });
  return { tabs: [...tabs, newTab], primaryActiveTabId: id, focusedGroup: 'primary' };
}

/**
 * Every persisted-path rewrite, applied in schema order.
 *
 * Kept out of `migrate` because each renamed route adds another step: the
 * version chain in `migrate` is about the state's *shape*, this is about where
 * tabs point. A tab whose path no longer resolves shows "not available", so a
 * rewrite is what keeps a rename from silently eating someone's workspace.
 */
function rewriteTabPaths(tabs: WorkspaceTab[], version: number): WorkspaceTab[] {
  let next = tabs;
  // /meals/nutrition was promoted to the top-level /food-diary route.
  if (version < 5) next = rewriteNutritionTabs(next);
  // /inbox and /email/* were unified onto the /attention surface.
  if (version < 6) next = rewriteAttentionTabs(next);
  // Profile, the Assistant/Administration hubs, /cost and the /my-data
  // configuration pages became one Settings page.
  if (version < 8) next = rewriteSettingsTabs(next);
  // `/chat/new` is gone: the conversation page is the new-chat surface.
  if (version < 9) next = rewriteChatNewTabs(next);
  // Tabs gained a derived destinationId in v7, and the rewrites above land on
  // new destinations, so anything migrated at all is re-stamped here.
  return version < 9 ? next.map(withDestination) : next;
}

export const useWorkspaceStore = create<WorkspaceStore>()(
  persist(
    (set, get) => ({
      tabs: [INITIAL_TAB],
      primaryActiveTabId: INITIAL_TAB.id,
      secondaryActiveTabId: null,
      focusedGroup: 'primary',
      splitRatio: 0.5,

      setFocusedGroup: group => {
        // Only focus secondary if it has tabs.
        if (group === 'secondary' && !get().secondaryActiveTabId) return;
        set({ focusedGroup: group });
      },

      setActiveTabId: (group, id) => {
        const tab = get().tabs.find(t => t.id === id && t.group === group);
        if (!tab) return;
        if (group === 'primary') {
          set({ primaryActiveTabId: id });
        } else {
          set({ secondaryActiveTabId: id });
        }
      },

      addTab: tab => {
        set(state => ({ tabs: [...state.tabs, withDestination(tab)] }));
      },

      openOrFocusDestination: destinationId => {
        const state = get();
        const patch = openOrFocusPatch(state.tabs, state.primaryActiveTabId, destinationId);
        if (patch) set(patch);
      },

      closeTab: id => {
        const state = get();
        const tab = state.tabs.find(t => t.id === id);
        if (!tab) return;

        const groupTabs = tabsForGroup(state.tabs, tab.group);
        const activeId = getActiveId(state, tab.group);

        // Secondary: if this is the last tab, close the pane entirely.
        if (tab.group === 'secondary' && groupTabs.length <= 1) {
          set({
            tabs: state.tabs.filter(t => t.id !== id),
            secondaryActiveTabId: null,
            focusedGroup: 'primary',
          });
          return;
        }

        // Primary: can't close the last tab.
        if (tab.group === 'primary' && groupTabs.length <= 1) return;

        const index = groupTabs.findIndex(t => t.id === id);
        const nextGroupTabs = groupTabs.filter(t => t.id !== id);
        const nextTabs = state.tabs.filter(t => t.id !== id);

        // Pick new active if we're closing the active tab.
        let nextActiveId = activeId;
        if (activeId === id) {
          const neighborIndex = Math.min(index, nextGroupTabs.length - 1);
          const neighbor = nextGroupTabs[neighborIndex];
          if (neighbor) nextActiveId = neighbor.id;
        }

        if (tab.group === 'primary') {
          set({ tabs: nextTabs, primaryActiveTabId: nextActiveId! });
        } else {
          set({ tabs: nextTabs, secondaryActiveTabId: nextActiveId });
        }
      },

      updateTab: (tabId, changes) => {
        const tab = get().tabs.find(t => t.id === tabId);
        if (!tab || isSameLocation(tab, changes)) return;
        // A navigation that lands on the path the tab already shows is still a
        // navigation: it can carry different state, and it earns a new key.
        const locationKey = changes.locationKey ?? mintLocationKey(tabId);
        set(state => ({
          tabs: state.tabs.map(t =>
            t.id === tabId ? withDestination({ ...t, ...changes, locationKey }) : t
          ),
        }));
      },

      reorderTabs: (activeId, overId) => {
        if (activeId === overId) return;
        const { tabs } = get();
        const activeTab = tabs.find(t => t.id === activeId);
        const overTab = tabs.find(t => t.id === overId);
        if (!activeTab || !overTab) return;
        if (activeTab.group !== overTab.group) return;
        if (activeTab.pinned !== overTab.pinned) return;

        const oldIndex = tabs.findIndex(t => t.id === activeId);
        const newIndex = tabs.findIndex(t => t.id === overId);
        const next = [...tabs];
        next.splice(oldIndex, 1);
        next.splice(newIndex, 0, activeTab);
        set({ tabs: next });
      },

      togglePin: id => {
        const { tabs } = get();
        const tab = tabs.find(t => t.id === id);
        if (!tab) return;

        const toggled = { ...tab, pinned: !tab.pinned };
        const rest = tabs.filter(t => t.id !== id);
        // Insert after the last pinned tab in the same group.
        const sameGroupRest = rest.filter(t => t.group === tab.group);
        const lastPinnedIndex = sameGroupRest.reduce((acc, t, i) => (t.pinned ? i : acc), -1);

        // Rebuild: find the global index of where to insert.
        const lastPinned = lastPinnedIndex >= 0 ? sameGroupRest[lastPinnedIndex] : undefined;
        const firstInGroup = sameGroupRest[0];
        const globalInsertAfter = lastPinned
          ? rest.indexOf(lastPinned)
          : firstInGroup
            ? rest.indexOf(firstInGroup) - 1
            : rest.length - 1;
        const next = [...rest];
        next.splice(globalInsertAfter + 1, 0, toggled);
        set({ tabs: next });
      },

      moveToSecondary: tabId => {
        const state = get();
        const tab = state.tabs.find(t => t.id === tabId);
        if (!tab) return;

        // Already in secondary — just focus it.
        if (tab.group === 'secondary') {
          set({ secondaryActiveTabId: tabId, focusedGroup: 'secondary' });
          return;
        }

        // Move tab from primary to secondary.
        const primaryTabs = tabsForGroup(state.tabs, 'primary');

        // Don't move the last primary tab.
        if (primaryTabs.length <= 1) return;

        const movedTab: WorkspaceTab = { ...tab, group: 'secondary' };
        const nextTabs = state.tabs.map(t => (t.id === tabId ? movedTab : t));

        // If we moved the active primary tab, pick a neighbor.
        let nextPrimaryActive = state.primaryActiveTabId;
        if (state.primaryActiveTabId === tabId) {
          const remainingPrimary = primaryTabs.filter(t => t.id !== tabId);
          const first = remainingPrimary[0];
          if (first) {
            nextPrimaryActive = first.id;
          }
        }

        set({
          tabs: nextTabs,
          primaryActiveTabId: nextPrimaryActive,
          secondaryActiveTabId: tabId,
          focusedGroup: 'secondary',
        });
      },

      closeSidePane: () => {
        const state = get();
        // Move all secondary tabs back to primary.
        const nextTabs = state.tabs.map(t =>
          t.group === 'secondary' ? { ...t, group: 'primary' as PaneGroup } : t
        );
        set({
          tabs: nextTabs,
          secondaryActiveTabId: null,
          focusedGroup: 'primary',
        });
      },

      setSplitRatio: ratio => {
        set({ splitRatio: Math.max(0.2, Math.min(0.8, ratio)) });
      },
    }),
    {
      name: 'workspace-tabs',
      version: 10,
      partialize: state => ({
        tabs: state.tabs.map(forStorage),
        primaryActiveTabId: state.primaryActiveTabId,
        secondaryActiveTabId: state.secondaryActiveTabId,
        focusedGroup: state.focusedGroup,
        splitRatio: state.splitRatio,
      }),
      migrate: (persisted, version) => {
        const state = persisted as Record<string, unknown>;
        let migrated: WorkspaceState;
        if (version < 3) {
          // Previous versions had flat tabs without groups. Assign all to primary.
          const tabs = (state.tabs as WorkspaceTab[] | undefined) ?? [INITIAL_TAB];
          const migratedTabs = tabs.map(t => ({ ...t, group: 'primary' as PaneGroup }));
          migrated = {
            tabs: migratedTabs,
            primaryActiveTabId: (state.activeTabId as string) ?? INITIAL_TAB.id,
            secondaryActiveTabId: null,
            focusedGroup: 'primary' as PaneGroup,
            splitRatio: (state.splitRatio as number) ?? 0.5,
          };
        } else {
          migrated = persisted as WorkspaceState;
        }
        if (version < 4) {
          // The /assistant shell was removed; Quotes is now a top-level /quotes route.
          migrated = {
            ...migrated,
            tabs: migrated.tabs.map(tab =>
              tab.path === '/assistant/quotes' || tab.path === '/assistant'
                ? { ...tab, path: '/quotes' }
                : tab
            ),
          };
        }
        migrated = { ...migrated, tabs: rewriteTabPaths(migrated.tabs, version) };
        if (version < 10) {
          // One tab per place: duplicates opened while the strip could close
          // them would otherwise linger, unseen and uncloseable.
          migrated = {
            ...migrated,
            tabs: dedupePrimaryTabs(migrated.tabs, migrated.primaryActiveTabId),
          };
        }

        return migrated;
      },
    }
  )
);
